// React
import { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import { Crumbs, PageHead } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetCommunityStructureQuery } from "state/communities/communitiesApiSlice";

// Utils
import { generateOrganization } from "pages/Structure/Node/utils";

const NODE_VISUALS = {
  COMMUNITY: { icon: "apartment", tint: "rec-tint-teal" },
  METER_CPE: { icon: "electric_meter", tint: "rec-tint-info" },
  METER_INTERNAL: { icon: "electric_meter", tint: "rec-tint-amber" },
  RESOURCE: { icon: "energy_savings_leaf", tint: "rec-tint-green" },
};

function TreeCard({ icon, tint, title, sub }) {
  return (
    <div className="rec-tree-card">
      <div className={`rec-tree-ic ${tint}`}>
        <Icon name={icon} fill />
      </div>
      <div className="tx">
        <b>{title}</b>
        {sub && <small>{sub}</small>}
      </div>
    </div>
  );
}

TreeCard.propTypes = {
  icon: PropTypes.string.isRequired,
  tint: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  sub: PropTypes.node,
};

TreeCard.defaultProps = {
  sub: null,
};

function nodeSub(org, t) {
  if (org.type === "COMMUNITY") return t("structure.legend.community");
  if (org.type === "METER_CPE") {
    return org.responsibleMember
      ? `${t("structure.legend.cpe")} · ${org.responsibleMember}`
      : t("structure.legend.cpe");
  }
  if (org.type === "METER_INTERNAL") return t("structure.legend.internal");
  if (org.type === "RESOURCE") {
    const owners = org.owners ?? [];
    if (owners.length === 1) return owners[0].owner_name;
    if (owners.length > 1)
      return t("structure.owners_count", {
        defaultValue: "{{count}} proprietários",
        count: owners.length,
      });
    return t("structure.legend.resource");
  }
  return null;
}

function TreeNode({ org, t }) {
  const visual = NODE_VISUALS[org.type] || NODE_VISUALS.RESOURCE;
  const children = org.organizationChildRelationship ?? [];
  return (
    <>
      <TreeCard
        icon={visual.icon}
        tint={visual.tint}
        title={org.tradingName}
        sub={nodeSub(org, t)}
      />
      {children.length > 0 && (
        <ul>
          {children.map((child, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${child.type}-${child.tradingName}-${i}`}>
              <TreeNode org={child} t={t} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

TreeNode.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  org: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

const LEGEND = [
  { icon: "apartment", tint: "rec-tint-teal", key: "community" },
  { icon: "electric_meter", tint: "rec-tint-info", key: "cpe" },
  { icon: "electric_meter", tint: "rec-tint-amber", key: "internal" },
  { icon: "energy_savings_leaf", tint: "rec-tint-green", key: "resource" },
];

function Structure() {
  const [zoom, setZoom] = useState(1);
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["community"]);

  const containerRef = useRef(null);

  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    const el = containerRef.current;
    const start = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
    el.style.cursor = "grabbing";

    const onMove = (ev) => {
      el.scrollLeft = start.sl - (ev.clientX - start.x);
      el.scrollTop = start.st - (ev.clientY - start.y);
    };
    const onUp = () => {
      el.style.cursor = "grab";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  const { data: structureData } = useGetCommunityStructureQuery(community.id);
  const org = generateOrganization(structureData, community);

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="community-structure" />
      <Crumbs items={[{ label: community?.name || "—" }, { label: t("structure.title") }]} />
      <PageHead
        title={t("structure.title")}
        subtitle={t("structure.subtitle")}
        actions={
          <div className="rec-seg">
            <button
              type="button"
              className="rec-seg-btn"
              title="-"
              onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}
            >
              <Icon name="remove" style={{ fontSize: 16 }} />
            </button>
            <button type="button" className="rec-seg-btn" onClick={() => setZoom(1)}>
              {`${Math.round(zoom * 100)}%`}
            </button>
            <button
              type="button"
              className="rec-seg-btn"
              title="+"
              onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2)))}
            >
              <Icon name="add" style={{ fontSize: 16 }} />
            </button>
          </div>
        }
      />

      <div className="rec-card overflow-hidden">
        <div
          ref={containerRef}
          onMouseDown={onMouseDown}
          role="presentation"
          className="overflow-auto"
          style={{
            cursor: "grab",
            padding: "28px 20px",
            minHeight: 480,
            background: "var(--surface-2)",
          }}
        >
          <div
            className="w-fit mx-auto"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "top center",
              transition: "transform 0.2s",
            }}
          >
            <div className="rec-orgtree">
              <TreeNode org={org} t={t} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-[18px] mt-[14px] px-1">
        {LEGEND.map((l) => (
          <div key={l.key} className="flex items-center gap-[7px] text-[12.5px] text-ink-2">
            <div className={`rec-tree-ic ${l.tint}`} style={{ width: 24, height: 24 }}>
              <Icon name={l.icon} fill style={{ fontSize: 15 }} />
            </div>
            {t(`structure.legend.${l.key}`)}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Structure;
